import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {BillingActModel} from "@services/back-office/model/billingActModel";
import {BehaviorSubject, catchError, EMPTY, Subject, takeUntil} from "rxjs";
import {UntypedFormControl} from "@angular/forms";
import {map} from "rxjs/operators";
import { HttpErrorResponse } from "@angular/common/http";
import {SharedService} from "@shared/shared.service";
import {BillingService} from "@shared/services/billing.service";
import {ToastrService} from "ngx-toastr";
import {TranslateService} from "@ngx-translate/core";
import {Store} from "@ngrx/store";
import {Reducers} from "@app/reducers";
import {Actions, ofType} from "@ngrx/effects";
import * as actsActions from '@billing/ngrx/actions/acts.actions';
import {NameResponse} from "@services/back-office/model/nameResponse";
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
    selector: 'app-act-dialog',
    templateUrl: './act-dialog.component.html',
    styleUrls: ['./act-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ActDialogComponent implements OnInit, OnDestroy {
    loading$ = new BehaviorSubject(false);
    closeDialogFlag: boolean;
    private destroyed$ = new Subject<void>();

    amountEdit: boolean;
    amount: UntypedFormControl = new UntypedFormControl();
    comment: UntypedFormControl = new UntypedFormControl();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            act: BillingActModel,
            contract: NameResponse
        },
        private dialogRef: MatDialogRef<ActDialogComponent>,
        private billingService: BillingService,
        private toastrService: ToastrService,
        private translateService: TranslateService,
        private store$: Store<Reducers>,
        private actions$: Actions
    ) {
    }

    ngOnInit(): void {
        if (this.data.act?.id) {
            this.loading$.next(true);
            this.billingService.getAct(this.data.act.id).pipe(
                takeUntil(this.destroyed$),
                map((res: BillingActModel) => {
                    this.data.act = res;
                    this.loading$.next(false);
                }),
                catchError((error: HttpErrorResponse) => {
                    SharedService.showErrorWithTitle(
                        this.translateService,
                        this.toastrService,
                        `responseErrors.${error.error.message}`
                    );
                    this.closeDialog(false);
                    return EMPTY;
                })
            ).subscribe();
        }

        this.watchEditActSuccess();
        this.watchEditActFailure();
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    submitForm(closeDialog?: boolean) {
        if (!this.amount.value) {
            return;
        }

        if (!this.comment.value) {
            this.toastrService.warning('Для редагування суми акту коментар обовʼязковий');
            return;
        }

        this.closeDialogFlag = !!closeDialog;
        this.loading$.next(true);

        const request: BillingActModel = {
            id: this.data.act.id,
            amount: this.amount.value,
            comment: this.comment.value
        };

        this.store$.dispatch(new actsActions.EditAct(request));
    }

    watchEditActSuccess() {
        this.actions$.pipe(
            ofType(actsActions.ActsActionTypes.EditActSuccess),
            takeUntil(this.destroyed$)
        ).subscribe((action: actsActions.EditActSuccess) => {
            this.data.act = action.payload;

            if (this.closeDialogFlag) {
                this.closeDialog(true);
            } else {
                this.closeEditAmount();
                this.loading$.next(false);
            }
        });
    }

    watchEditActFailure() {
        this.actions$.pipe(
            ofType(actsActions.ActsActionTypes.EditActFailure),
            takeUntil(this.destroyed$)
        ).subscribe(() => {
            this.loading$.next(false);
        });
    }

    closeEditAmount() {
        this.amount.reset();
        this.comment.reset();
        this.amountEdit = false;
    }

    openLink() {
        if (this.data.act?.link) {
            window.open(this.data.act.link, '_blank');
        }
    }

    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
    }
}
